import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, Loader2 } from "lucide-react";
import { gsap } from "gsap";
import toast from "react-hot-toast";
import { InputNew } from "./ui/inputNew";
import { TextareaNew } from "./ui/textareaNew";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const ContactForm = () => {
  const submitBtnRef = useRef<HTMLButtonElement | null>(null);
  const textRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const text = textRef.current;

    if (!text) return;

    gsap.set([text], {
      opacity: 0,
      x: 50,
    });

    const tl = gsap.timeline({ delay: 0.5 });

    tl.to(text, {
      opacity: 1,
      x: 0,
      duration: 1,
      ease: "power3.out",
    });
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    const url =
      "https://script.google.com/macros/s/AKfycbw2U75QMxqxonwyQd9-nRcRTiO-_beVK5bP-hKNHTjRfTQFCQlieKprWTvpThBUUXLj/exec";

    try {
      const body = new URLSearchParams({
        Name: formData.name,
        Email: formData.email,
        Phone: formData.phone,
        Message: formData.message,
      });

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const data = await res.text();

      if (res.ok) {
        toast.success("Message sent successfully!");
        setFormData({ name: "", email: "", phone: "", message: "" }); // reset form
      } else {
        throw new Error(data || "Something went wrong");
      }
    } catch (error: any) {
      toast.error("Failed to send message. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }

    if (submitBtnRef.current) {
      gsap.to(submitBtnRef.current, {
        scale: 0.95,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        ease: "power2.out",
      });
    }
  };

  return (
    <div ref={textRef} className="bg-[#f5f5f5] w-full">
      <h2 className="text-5xl text-[#0d1821] mb-10 font-medium ">
        Ready
        <div className="pt-[6px]" />
        <span className="text-[#00a878]">to Grow ?</span>
      </h2>
      <p className="mb-3 text-md md:text-lg text-[#0d1821]/90 mx-auto font-poppins">
        Drop us a message and let's boost your brand together.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <div className="form-field">
            <InputNew
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Name *"
            />
          </div>
          <div className="form-field">
            <InputNew
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email *"
            />
          </div>
        </div>

        <div className="form-field">
          <InputNew
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Phone *"
          />
        </div>

        <div className="form-field">
          <TextareaNew
            id="message"
            name="message"
            required
            rows={4}
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Message *"
          />
        </div>

        <Button
          ref={submitBtnRef}
          type="submit"
          disabled={loading}
          className="submit-btn btn-hero w-full py-3 text-lg font-medium group text-[#0d1821] disabled:opacity-70"
        >
          {loading ? (
            <Loader2 className="mr-2 w-5 h-5 animate-spin text-[#0d1821]" />
          ) : (
            <Send className="mr-2 w-5 h-5 group-hover:translate-x-1 transition-transform text-[#0d1821]" />
          )}
          {loading ? "Sending..." : "Send Message"}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;
